import { useEffect, useMemo, useState } from "react";
import { MantineReactTable, useMantineReactTable } from "mantine-react-table";
import _ from "lodash";
import { Textarea, useMantineColorScheme, Box, Loader } from "@mantine/core";
import { useTranslation } from "react-i18next";
import MGAAPI from "../../api";
import { projectDetailsAtom } from "../../utils/atoms/projectDetailsAtom";
import { useAtom } from "jotai";
import { userScopeAtom } from "../../utils/atoms/userScopeAtom";
import {
  useAssessmentRouteParams,
  useCurrentDesignVersion,
  isHtmlTag,
  isDesignVersionReadOnly,
} from "../../constants/helper";

const MGA_API = new MGAAPI();

const ScenariosTable = ({
  initialSensitivityData,
  callSnackbar,
  setInitialSensitivityData,
  showSimuationResult,
  setScenarioSelectionValid,
}) => {
  const { t } = useTranslation();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const { colorScheme } = useMantineColorScheme();
  const [userScope] = useAtom(userScopeAtom);
  const isLightMode = colorScheme === "light";
  const [tableData, setTableData] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const { projectId, designId, designVersionId } = useAssessmentRouteParams();
  const selectedVersion = useCurrentDesignVersion(
    projectDetails?.designWithVersionsDTOList
  );

  const isVersionReadOnly = isDesignVersionReadOnly(selectedVersion?.versionStatus);

  const tableInteractionStyles = {
    opacity: isVersionReadOnly ? 0.8 : 1,
  };

  const projectDesignVersion =
    projectDetails?.designWithVersionsDTOList?.[0]?.designVersionDTO?.[0];

  const handleValidateScenarioName = (value, currentIndex) => {
    if (isVersionReadOnly) return;
    const trimmedValue = value?.trim();
    const lowerCaseName = trimmedValue?.toLowerCase();
    const namePattern = /^[A-Za-z][A-Za-z0-9_. ]*$/;
    const isDuplicate = tableData.some(
      (row, index) =>
        index !== currentIndex &&
        row.scenarioName.trim().toLowerCase() === lowerCaseName
    );

    const getErrorMessage = () => {
      switch (true) {
        case !trimmedValue:
          return t("simulation.scenario.name.required");
        case isDuplicate:
          return t("simulation.scenario.name.unique");
        case isHtmlTag(trimmedValue):
          return t("simulation.scenario.name.noHtmlTags");
        case !namePattern.test(trimmedValue):
          return t("simulation.scenario.name.invalid");
        default:
          return null;
      }
    };

    const errorMessage = getErrorMessage();

    setValidationErrors((prevErrors) => {
      const updatedErrors = { ...prevErrors };
      if (errorMessage) {
        updatedErrors[currentIndex] = errorMessage;
      } else {
        delete updatedErrors[currentIndex];
      }
      return updatedErrors;
    });
  };           

  const groups = useMemo(() => {
    const allKeys = _.flatMap(initialSensitivityData, (row) =>
      Object.keys(row).filter(
        (rowItem) =>
          rowItem !== "scenarioName" &&
          rowItem !== "isChecked" &&
          rowItem !== "id" &&
          rowItem !== "description"
      )
    );
    return _.uniq(allKeys);
  }, [initialSensitivityData]);

  const columns = useMemo(() => {
    const groupedColumns = groups?.map((group) => {
      const fieldNames = _.uniq(
        _.flatMap(initialSensitivityData, (row) =>
          _.get(row, `${group}.fields`, []).map((f) => f?.name)
        )
      ).filter((name) => name !== "description");

      const subColumns = fieldNames.map((name) => ({
        id: `${group}_${name}`,
        header: name,
        accessorFn: (row) => {
          const field = _.find(_.get(row, `${group}.fields`, []), { name });
          return field?.value ?? "";
        },
        enableColumnFilter: false,
      }));

      return {
        id: group,
        header: group === "Baseload genset" ? "GENSET" : group.toUpperCase(),
        columns: subColumns,
      };
    });

    const baseColumns = [
      {
        accessorKey: "scenarioName",
        header: t("simulation.scenarios.header"),
        enableColumnFilter: false,
        pinned: "left",
        Cell: ({ row }) => {
          const index = row.index;
          const value = tableData?.[index]?.scenarioName ?? "";
          return (
            <Textarea
              value={value}
              disabled={isVersionReadOnly || !userScope?.access?.saveProjectSensitivity}
              minRows={1}
              autosize
              onChange={({ target: { value } }) => {
                if (isVersionReadOnly) return;
                setTableData((prev) =>
                  prev.map((item, i) =>
                    i === index ? { ...item, scenarioName: value } : item
                  )
                );
                handleValidateScenarioName(value, index);
              }}
              onBlur={({ target: { value } }) => {
                handleValidateScenarioName(value, index);
              }}
              error={validationErrors[index]}
              styles={{
                root: {
                  width: "100%",
                  pointerEvents: isVersionReadOnly ? "none" : "auto",
                },
                wrapper: {
                  width: "100%",
                },
                input: {
                  width: "100%",
                  boxSizing: "border-box",
                },
                error: {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                },
              }}
            />
          );
        },
      },
      {
        id: "scenarioDescription",
        accessorFn: (row) => row.description ?? "",
        header: t("simulation.scenarios.description"),
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const index = row.index;
          const value = tableData?.[index]?.description ?? "";
          const descError = value?.length > 500
            ? t("simulation.scenarios.description.maxLength")
            : isHtmlTag(value)
            ? t("simulation.scenarios.description.noHtmlTags")
            : null;
          return (
            <Textarea
              value={value}
              disabled={isVersionReadOnly || !userScope?.access?.saveProjectSensitivity}
              onChange={({ target: { value } }) => {
                if (isVersionReadOnly) return;
                setTableData((prev) =>
                  prev.map((item, i) =>
                    i === index ? { ...item, description: value } : item
                  )
                );
              }}
              error={descError}
              autosize
              styles={{
                input: {
                  width: "100%",
                  boxSizing: "border-box",
                  pointerEvents: isVersionReadOnly ? "none" : "auto",
                },
                root: {
                  width: "100%",
                },
                error: {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  maxWidth: "100%",
                },
              }}
            />
          );
        },
      },
    ];
    return [...baseColumns, ...groupedColumns];
  }, [groups, tableData, initialSensitivityData, validationErrors, isVersionReadOnly, userScope?.access?.saveProjectSensitivity]);

  const handleSaveSensitivityData = () => {
    if (isVersionReadOnly) {
      callSnackbar("error", t("progressBar.editingRestricted"));
      return;
    }
    const selectedItems = _.filter(tableData, {
      isChecked: true,
    })?.length;
    if ( projectId && designId && designVersionId && selectedItems) {
      setIsLoading(true);
      const payload = {
        projectId,
        designId,
        designVersionId,
        tableData,
      };
      MGA_API.saveSensitivityScenarioData(payload)
        .then((response) => {
          setIsLoading(false);
          setInitialSensitivityData(response);
          if (setScenarioSelectionValid) setScenarioSelectionValid(true);
          callSnackbar("success", "Simulation Scenario Data Updated Successfully");
        })
        .catch((error) => {
          setIsLoading(false);
          console.log("Error fetching simulation scenario data:", error);
        });
    } else {
      if (setScenarioSelectionValid) setScenarioSelectionValid(false);
      callSnackbar("error", t("simulation.scenarios.selectAtLeastOne"));
    }
  };

  useEffect(() => {
    if (isVersionReadOnly) return;
    const hasScenarioDataChanged = _.isEqual(initialSensitivityData, tableData);
    const hasNameError = validationErrors && Object.keys(validationErrors)?.length;
    const hasDescriptionError = (tableData || []).some(
      (row) => row.description?.length > 500 || isHtmlTag(row.description ?? "")
    );
    const isError = hasNameError || hasDescriptionError;
    const timer = setTimeout(() => {
      if (!isError && !hasScenarioDataChanged) {
        handleSaveSensitivityData();
      }
    }, 2000);

    return () => clearTimeout(timer);
  }, [tableData, isVersionReadOnly]);

  useEffect(() => {
    const allRowSelected = tableData?.map((row, index) => {
      const selectedIndex = !!rowSelection[index];
      return {
        ...row,
        isChecked: selectedIndex,
      };
    });
    setTableData(allRowSelected);
    if (setScenarioSelectionValid) {
      const anySelected = (allRowSelected || []).some((r) => r?.isChecked);
      setScenarioSelectionValid(!!anySelected);
    }
  }, [rowSelection]);

  useEffect(() => {
    if (initialSensitivityData?.length) {
      setValidationErrors({});
      setTableData(initialSensitivityData);
      let selectedScenario = {};
      initialSensitivityData?.forEach((row, index) => {
        if (row?.isChecked) {
          selectedScenario[index] = true;
        }
      });
      setRowSelection(selectedScenario);
      if (setScenarioSelectionValid) {
        const anySelected = initialSensitivityData?.some((r) => r?.isChecked);
        setScenarioSelectionValid(!!anySelected);
      }
    }
  }, [initialSensitivityData]);

  const table = useMantineReactTable({
    columns,
    data: tableData,
    enableRowSelection: !isVersionReadOnly && userScope?.access?.saveProjectSensitivity,
    onRowSelectionChange: (selection) => {
      if (!isVersionReadOnly) setRowSelection(selection);
    },
    state: { rowSelection },
    enableSorting: false,
    enableTopToolbar: false,
    enableColumnActions: false,
    enablePagination: false,
    enablePaginationControls: false,
    layoutMode: "grid",
    mantineTableProps: {
      withColumnBorders: true,
    },
    mantineSelectCheckboxProps: {
      disabled: isVersionReadOnly,
      style: {
        pointerEvents: isVersionReadOnly ? "none" : "auto",
      },
    },
    mantineSelectAllCheckboxProps: {
      disabled: isVersionReadOnly,
      style: {
        pointerEvents: isVersionReadOnly ? "none" : "auto",
      },
    },
    mantineTableContainerProps: {
      style: {
        ...tableInteractionStyles,
        position: "relative",
        overflow: "auto",
        maxHeight: "650px",
        overflowY: "auto",
      },
    },
    editDisplayMode: "cell",
    enableEditing: false,
    enableRowActions: false,
    enablePinning: true,
    initialState: {
      density: "xs",
      columnPinning: {
        left: ["mrt-row-select", "scenarioName"],
      },
    },
    mantineTableHeadProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
        position: "sticky",
        top: 0,
        zIndex: 5,
        boxShadow: "0 2px 4px rgba(0,0,0,0.06)",
      },
    },
    mantineTableHeadCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
        position: "sticky",
        top: 0,
        zIndex: 6,
      },
    },
    mantineTableBodyCellProps: {
      sx: {
        fontWeight: "normal",
        fontSize: "14px",
        fontFamily: "Nunito",
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
    mantineTableBodyProps: {
      sx: {
        backgroundColor: isLightMode
          ? "#FFFFFF"
          : "var(--se-background-alternative)",
      },
    },
  });

  return (
    <Box 
      style={{
        position: "relative",
        width: "100%",
      }}
    >
      <MantineReactTable table={table} />
      {isLoading && (
        <Box
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 100,
            borderRadius: "4px",
          }}
        >
          <Loader />
        </Box>
      )}
    </Box>
  );
};

export default ScenariosTable;
